import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useGetFormattedDate } from '@app/src/components/DateDisplay'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import type { DrawerViewProps } from '@app/src/components/Drawer/DrawerView'
import DrawerView from '@app/src/components/Drawer/DrawerView'
import LinkButton from '@app/src/components/LinkButton'
import { QuestionItemAnswer } from '@app/src/components/Table/Cells/AnswerWithFlagCell'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { DrawerHeader } from '@app/src/pages/TopicalRisk/Drawer/DrawerHeader'
import { DrawerSectionItem } from '@app/src/pages/TopicalRisk/Drawer/DrawerSectionItem'
import { BUTTON_PROPS } from '@app/src/pages/TopicalRisk/Drawer/drawerSharedStyles'
import type { AnswerFlagRuleResult } from '@app/src/types/answerFlags'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import qs from 'qs'
import React from 'react'
import { useIntl } from 'react-intl'

export type DrawerViewAnswerFlagProps = {
  answerFlagRuleResult: AnswerFlagRuleResult
} & DrawerViewProps

const DrawerViewAnswerFlag: React.FC<DrawerViewAnswerFlagProps> = ({ answerFlagRuleResult, ...props }) => {
  const { formatMessage } = useIntl()
  const { closeDrawer } = useDrawer()
  const { formatValueToDate } = useGetFormattedDate()

  // Rule result has a single responseItemId
  const responseItemId = answerFlagRuleResult.responseItemId

  const {
    items: responseItems,
    isLoading,
    isError,
  } = useFetchCollectionWithPost<ResponseItem>({
    key: FetchKey.ResponseItemsCollection,
    endpoint: endpoints.responseItemsCollection,
    payload: {
      filter: [
        {
          name: 'id',
          filters: [{ operator: Operators.EqualTo, value: responseItemId }],
        },
      ],
      include: [
        'requestItem.template.questionType',
        'requestItem.template.unit',
        'requestItem.template.requestCollection',
        'response.request',
      ],
    },
    options: { enabled: Boolean(responseItemId), staleTime: ONE_MINUTE_MS },
  })

  const responseItem = responseItems[0]

  return (
    <DrawerView {...props}>
      <DrawerHeader
        titleTranslationKey="answerRisk.title"
        lastUpdated={answerFlagRuleResult.createdAt}
        riskStatus={answerFlagRuleResult.riskStatus}
      />

      {isLoading ? (
        <Stack spacing={3} px={3} pt={2}>
          <Box>
            <Skeleton variant="text" width="30%" height={24} />
            <Skeleton variant="text" width="100%" height={60} />
          </Box>
          <Box>
            <Skeleton variant="text" width="30%" height={24} />
            <Skeleton variant="text" width="100%" height={60} />
          </Box>
          <Box>
            <Skeleton variant="text" width="30%" height={24} />
            <Skeleton variant="text" width="100%" height={60} />
          </Box>
        </Stack>
      ) : isError || !responseItem?.requestItem ? (
        <Box sx={{ px: 3, py: 2 }}>
          <Typography color="text.secondary">{formatMessage({ id: 'errors.failedToLoad' })}</Typography>
        </Box>
      ) : (
        <Stack>
          <DrawerSectionItem header={formatMessage({ id: 'general.question' })} sx={{ py: 2 }}>
            <Typography variant="body1">
              {responseItem?.requestItem?.template?.questionText ||
                responseItem?.requestItem?.description ||
                formatMessage({ id: 'general.notAvailable' })}
            </Typography>
          </DrawerSectionItem>

          <DrawerSectionItem header={formatMessage({ id: 'schemas.responseItem.answer' })} sx={{ py: 2, pb: 2.5 }}>
            {!responseItem?.requestItem ? (
              <Typography color="text.disabled">{formatMessage({ id: 'general.notAvailable' })}</Typography>
            ) : (
              <Box>
                {responseItem.requestItem?.template?.requestCollection?.items && (
                  <Typography variant="body2" color="text.secondary" pb={0.5}>
                    {formatMessage(
                      { id: 'assessments.answersSelected' },
                      {
                        selectedCount: responseItem.selectedOptions?.length,
                        totalAvailableCount: responseItem.requestItem?.template?.requestCollection?.items?.length,
                      },
                    )}
                  </Typography>
                )}
                <QuestionItemAnswer item={responseItem} maxRows={100} selectedOptionsAsChips skippedAnswerAsCard />
              </Box>
            )}
          </DrawerSectionItem>

          <DrawerSectionItem
            header={formatMessage({ id: 'schemas.response.request' })}
            sx={{ py: 2 }}
            actionButton={
              responseItem?.response?.request && (
                <LinkButton
                  {...BUTTON_PROPS}
                  onClick={closeDrawer}
                  to={{
                    pathname: paths.reporting,
                    search: qs.stringify({
                      requestId: responseItem.response.targetRequestId,
                      responseId: responseItem.responseId,
                    }),
                  }}
                >
                  {formatMessage({ id: 'schemas.providerProgress.viewResponse' })}
                </LinkButton>
              )
            }
          >
            {responseItem?.response?.request ? (
              <Box>
                <Box>
                  {responseItem.response.request.title}
                  {responseItem.response.request.periodName ? ` - ${responseItem.response.request.periodName}` : ''}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatMessage(
                    { id: 'reporting.sharing.reportedOn' },
                    { date: formatValueToDate({ value: responseItem.response?.createdAt ?? '' }) },
                  )}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {formatMessage({ id: 'general.notAvailable' })}
              </Typography>
            )}
          </DrawerSectionItem>
        </Stack>
      )}
    </DrawerView>
  )
}

export default DrawerViewAnswerFlag
